import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip
} from '@mui/material';
import {
  Accessibility,
  ArrowBack,
  Upload,
  Image,
  VolumeUp,
  Check,
  Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const AccessibilityPage: React.FC = () => {
  const navigate = useNavigate();
  const [altText, setAltText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accessibilityFeatures = [
    {
      title: 'Alt Text Generation',
      description: 'Automatically generate descriptive alt text for images',
      icon: <Image color="primary" />,
      status: 'active'
    },
    {
      title: 'Text-to-Speech',
      description: 'Convert text content to audio for screen readers',
      icon: <VolumeUp color="primary" />,
      status: 'active'
    },
    {
      title: 'Color Contrast Check',
      description: 'Verify text meets WCAG contrast requirements',
      icon: <Check color="success" />,
      status: 'active'
    },
    {
      title: 'Content Structure',
      description: 'Analyze content for proper heading hierarchy',
      icon: <Warning color="warning" />,
      status: 'warning'
    }
  ];

  const generateAltText = async () => {
    setGenerating(true);
    setError(null);

    try {
      // Mock alt text generation
      setTimeout(() => {
        setAltText('A professional business meeting with diverse team members collaborating around a conference table with laptops and documents.');
        setGenerating(false);
      }, 2000);
    } catch (err: any) {
      setError('Failed to generate alt text');
      setGenerating(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Accessibility color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Accessibility Tools
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Make your content accessible to everyone
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alt Text Generator
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  component="label"
                  sx={{ mb: 2 }}
                >
                  Upload Image
                  <input type="file" hidden accept="image/*" />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Upload an image to generate descriptive alt text
                </Typography>
              </Box>
              
              {altText && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Generated Alt Text:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Box>
              )}
              
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} /> : <Image />}
                onClick={generateAltText}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate Alt Text'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Accessibility Features
              </Typography>
              <List>
                {accessibilityFeatures.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.title}
                      secondary={feature.description}
                    />
                    <Chip
                      label={feature.status}
                      color={feature.status === 'active' ? 'success' : 'warning'}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Accessibility Guidelines
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Alt Text"
                    secondary="Describe images clearly and concisely"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Color Contrast"
                    secondary="Ensure 4.5:1 ratio for normal text"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Heading Structure"
                    secondary="Use proper H1-H6 hierarchy"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Keyboard Navigation"
                    secondary="All features accessible via keyboard"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AccessibilityPage; 